/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {useEffect, useMemo, useState} from 'react';

import {mediaQueryRegistry} from '@amblique/base/app/helpers/media-query-registry';
import {responsiveHelper} from '@amblique/base/app/helpers/responsive-helper';

/**
 * Parse responsive value string into structured object or get current value
 *
 * Supports CSS property-like syntax for defining responsive values:
 * - Named descriptors: "mobile: 320, tablet: 640, desktop: 1280"
 * - Shorthand: "320, 640, 1280" (mobile, tablet, desktop)
 * - Default modifier: "mobile: 320, tablet: 640 !default"
 *
 * @param {string} input - Responsive value string to parse
 * @param {string} [format='breakpoint'] - Output format: 'device', 'breakpoint', or 'value'
 * @param {string} [type='string'] - Value type: 'string', 'integer', 'number', 'boolean'
 * @returns {object|*} Parsed object or current value (depends on format)
 *
 * @example Get current value (recommended - replaces unreliable useBreakpointValue)
 * const currentId = useResponsiveValue('mobile: id1; tablet: id2; desktop: id3', 'value');
 * // => 'id1' (at mobile), 'id2' (at tablet), or 'id3' (at desktop)
 * // Updates automatically when breakpoint changes
 *
 * @example Parse to breakpoint format (for manual use with useBreakpointValue)
 * const responsiveId = useResponsiveValue('mobile: id1, tablet: id2, desktop: id3');
 * // => { base: 'id1', md: 'id2', xl: 'id3' }
 *
 * @example Parse to device format (with metadata)
 * const result = useResponsiveValue('mobile: 320, tablet: 640, desktop: 1280', 'device', 'integer');
 * // => { isResponsive: true, isEmpty: false, breakpoints: ['mobile', 'tablet', 'desktop'],
 * //      value: null, mobile: 320, tablet: 640, desktop: 1280 }
 *
 * @example Shorthand syntax with current value
 * const currentId = useResponsiveValue('id1, id2, id3', 'value');
 * // => 'id1' (mobile), 'id2' (tablet), or 'id3' (desktop)
 *
 * @example Type coercion with current value
 * const shouldAutoplay = useResponsiveValue('mobile: false; desktop: true', 'value', 'boolean');
 * // => false (mobile) or true (desktop)
 */
export const useResponsiveValue = (input, format = 'breakpoint', type = 'string') => {
  // Parse the input value
  const parsed = useMemo(() => {
    // Handle null/undefined/empty input
    if (!input) {
      return format === 'device' ? {isResponsive: false, isEmpty: true, breakpoints: [], value: null} : {};
    }

    // Backward compatibility: if input is already an object, return as-is
    if (typeof input === 'object') {
      return input;
    }

    // Parse the responsive value string
    // For 'value' format, parse to 'breakpoint' internally since mediaQueryRegistry uses breakpoint names
    return responsiveHelper.parseValue(input, {type, format: format === 'value' ? 'breakpoint' : format});
  }, [input, format, type]);

  // If format is 'value', get current matched value and subscribe to changes
  const [currentValue, setCurrentValue] = useState(() => {
    if (format === 'value') {
      return mediaQueryRegistry.getMatchedValue(parsed);
    }

    return parsed;
  });

  useEffect(() => {
    if (format !== 'value') {
      return;
    }

    // Update current value when parsed changes
    setCurrentValue(mediaQueryRegistry.getMatchedValue(parsed));

    // Subscribe to breakpoint changes
    // Callback receives { breakpoint, value }
    const unsubscribe = mediaQueryRegistry.subscribe(({value}) => {
      setCurrentValue(value);
    }, parsed);

    return unsubscribe;
  }, [format, parsed]);

  return format === 'value' ? currentValue : parsed;
};
